import React, { useEffect, useMemo, useState } from 'react';
import { Input } from 'antd';
import { ISpacePoint } from '@mono/types/src/weather-widgets';
import { toHash } from '@mono/to-hash';
import styled from 'styled-components';
import { normalizeLongitude180 } from '@mono/normalize-longitude';

const parseLatInput = (latInput: string) => {
    const parsed = Number(latInput);
    if (isNaN(parsed)) return null;
    if (parsed > 90 || parsed < -90) return null;
    return parsed;
};
const parseLonInput = (lonInput: string) => {
    const parsed = normalizeLongitude180(Number(lonInput));
    if (isNaN(parsed)) return null;
    if (parsed > 180 || parsed < -180) return null;
    return parsed;
};

interface Props {
    style?: React.CSSProperties;
    space?: ISpacePoint;
    setSpace?: (space: ISpacePoint | undefined) => void;
    gap?: 2 | 4;
}
export const LatLonEditor = ({ style, space, setSpace, gap = 2 }: Props) => {
    const [latInput, setLatInput] = useState<string | undefined>(space ? space.coordinates[1].toString() : undefined);
    const [lonInput, setLonInput] = useState<string | undefined>(space ? space.coordinates[0].toString() : undefined);

    const validatedLat = useMemo(() => {
        if (latInput === undefined) return;
        const parsedLat = parseLatInput(latInput);
        if (!parsedLat) return 'failed to parse';

        return Number(parsedLat.toFixed(6));
    }, [latInput]);

    const validatedLon = useMemo(() => {
        if (lonInput === undefined) return;
        const parsedLon = parseLonInput(lonInput);
        if (!parsedLon) return 'failed to parse';

        return Number(parsedLon.toFixed(6));
    }, [lonInput]);

    useEffect(() => {
        if (!setSpace) return;

        // If both are undefined, then we want to set the space to undefined
        if (validatedLat === undefined && validatedLon === undefined) {
            setSpace(undefined);
            return;
        }

        // If one is undefined, do nothing, the user is still editing
        if (validatedLat === undefined || validatedLon === undefined) return;

        // if either of them have errors, do nothing, the user is still editing
        if (typeof validatedLat === 'string' || typeof validatedLon === 'string') return;

        const newSpace: ISpacePoint = { type: 'Point', coordinates: [validatedLon, validatedLat] };

        //No need to update if they are equal
        if (toHash(newSpace) === toHash(space)) return;

        setSpace(newSpace);
    }, [validatedLat, validatedLon]);

    useEffect(() => {
        if (!space) {
            setLatInput(undefined);
            setLonInput(undefined);
            return;
        }

        setLonInput(space.coordinates[0].toString());
        setLatInput(space.coordinates[1].toString());
    }, [toHash(space)]);

    return (
        <LatLonEditorDiv style={{ ...style }} gap={gap}>
            <AntInputOverride
                disabled={!setSpace}
                value={latInput}
                onChange={(e) => setLatInput(e.target.value)}
                status={typeof validatedLat === 'string' ? 'error' : undefined}
                addonBefore={<span style={{ width: 48, display: 'inline-block' }}>Lat</span>}
            />
            <AntInputOverride
                disabled={!setSpace}
                value={lonInput}
                onChange={(e) => setLonInput(e.target.value)}
                status={typeof validatedLon === 'string' ? 'error' : undefined}
                addonBefore={<span style={{ width: 48, display: 'inline-block' }}>Lon</span>}
            />
        </LatLonEditorDiv>
    );
};

const AntInputOverride = styled(Input)`
    .ant-input-group-addon {
        width: 48px;
        background-color: '#eee';
        color: black !important;
    }
`;

const LatLonEditorDiv = styled.div<{ gap: 2 | 4 }>`
    display: flex;
    width: 100%;
    justify-content: start;
    flex-direction: row;
    gap: ${(props) => props.gap}px;
`;
